/**
 * Admin Bootstrap — visual, one-click admin password reset.
 *
 * Reachable at `/admin-reset` — deliberately not linked from any menu.
 * Anyone with the URL still needs the shared secret, which is baked into
 * the page (same secret the /api/system/bootstrap-admin route accepts).
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const BOOTSTRAP_SECRET = "HTP-BOOTSTRAP-2026-8FC4D2A9";

export default function AdminBootstrap() {
  const [email, setEmail] = useState("admin@htpsportshub.in");
  const [password, setPassword] = useState("Admin@12345");
  const [confirm, setConfirm] = useState("Admin@12345");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/system/bootstrap-admin", {
        secret: BOOTSTRAP_SECRET,
        email: email.trim(),
        password,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-black/60 border-2 border-brand-orange/60 p-6 shadow-2xl shadow-brand-orange/10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-brand-orange text-black flex items-center justify-center">
            <ShieldCheck size={26} />
          </div>
          <div>
            <div className="font-display font-black text-2xl leading-none">Admin Reset</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-1">
              HTP Sports Hub · Emergency access
            </div>
          </div>
        </div>

        <div className="bg-yellow-500/10 border-l-4 border-yellow-500 px-3 py-2 mb-6 text-xs text-yellow-100">
          Use this page ONLY when you&apos;re locked out of the admin panel. It force-resets the admin credentials on this deployment.
        </div>

        {result ? (
          <div className="text-center py-6">
            <CheckCircle2 className="text-green-400 mx-auto mb-4" size={54} />
            <div className="font-display font-black text-xl mb-2">Admin {result.action}</div>
            <div className="text-sm text-white/70 mb-6">{result.message}</div>
            <div className="text-left bg-white/5 border border-white/10 p-3 mb-6 font-mono text-xs">
              <div><span className="text-white/50">Email:</span> {result.email}</div>
              <div><span className="text-white/50">Password:</span> the one you just set</div>
            </div>
            <Link
              to="/admin/login"
              data-testid="admin-bootstrap-goto-login"
              className="block bg-brand-orange hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-sm px-4 py-3"
            >
              Go to admin login →
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">
                Admin email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-bootstrap-email"
                className="w-full bg-black/60 border border-white/15 px-3 py-2 outline-none focus:border-brand-orange text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">
                New password (min 8 characters)
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-bootstrap-password"
                className="w-full bg-black/60 border border-white/15 px-3 py-2 outline-none focus:border-brand-orange text-sm"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">
                Confirm password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="admin-bootstrap-confirm"
                className="w-full bg-black/60 border border-white/15 px-3 py-2 outline-none focus:border-brand-orange text-sm"
                required
                minLength={8}
              />
            </div>

            {error && (
              <div className="bg-red-500/15 border-l-4 border-red-500 px-3 py-2 flex items-start gap-2 text-red-100 text-xs">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="admin-bootstrap-submit"
              className="w-full bg-brand-orange hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-sm px-4 py-3 flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
              {busy ? "Resetting…" : "Reset admin credentials"}
            </button>

            <div className="text-[11px] text-white/40 text-center">
              After reset, log in at <Link className="text-brand-orange underline" to="/admin/login">/admin/login</Link> and change the password from Admin → Profile.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
